import { Link, createFileRoute } from '@tanstack/react-router';
import { buildHead } from '~/seo/site';
import { FAQ_JSONLD, FaqBody } from './faq';

// Combined Rules + FAQ page. The rules half targets the "beat battle rules"
// search intent (its own Article schema); the FAQ half (with FAQPage schema)
// is folded in below so there is one place for both. /faq redirects here.
export const Route = createFileRoute('/rules')({
  head: () => {
    const head = buildHead({
      title: 'Prod Battle Rules & FAQ - How Online Beat Battles Work',
      description:
        'The official Prod Battle rules and FAQ: match phases, every game mode, blind voting, scoring, the honor system, fair play, and answers to common questions. Same kit for everyone - skill, not gear.',
      path: '/rules',
    });
    return {
      ...head,
      meta: [...head.meta, { 'script:ld+json': RULES_JSONLD }, { 'script:ld+json': FAQ_JSONLD }],
    };
  },
  component: Rules,
});

const RULES_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Prod Battle Rules - how online beat battles work',
  description:
    'Match phases, game modes, blind voting, scoring, honor system, and fair-play rules for online beat battles.',
  author: { '@type': 'Organization', name: 'Prod Battle' },
  publisher: { '@type': 'Organization', name: 'Prod Battle' },
};

const TOC = [
  { id: 'rules-match-flow', label: 'How a match runs' },
  { id: 'rules-modes', label: 'Game modes' },
  { id: 'rules-voting', label: 'Voting rules' },
  { id: 'rules-honor', label: 'Honor system' },
  { id: 'rules-fair-play', label: 'Fair play' },
  { id: 'faq', label: 'FAQ' },
];

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mt-10 scroll-mt-24 text-2xl font-bold tracking-tight">
      {children}
    </h2>
  );
}

function Rules() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12">
      <header className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight">Prod Battle Rules &amp; FAQ</h1>
        <p className="text-muted-foreground">
          Every battle on Prod Battle follows the same core ruleset: everyone gets the{' '}
          <strong>same sample kit</strong>, the same timer, and votes are <strong>blind</strong>.
          Skill, not gear. The rules are below; the{' '}
          <a href="#faq" className="underline hover:text-foreground">
            FAQ
          </a>{' '}
          covers everything else.
        </p>
        <nav className="flex flex-wrap gap-2 pt-2 text-xs">
          {TOC.map((t) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary hover:text-foreground"
            >
              {t.label}
            </a>
          ))}
        </nav>
      </header>

      <H2 id="rules-match-flow">How a match runs</H2>
      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Lobby</strong> - producers join a room. Quickplay
          auto-starts when enough players are in; private rooms start when every seated player is
          ready.
        </li>
        <li>
          <strong className="text-foreground">Build</strong> - the timer starts and everyone
          produces with the same kit. Default windows vary by mode; the room shows the countdown.
        </li>
        <li>
          <strong className="text-foreground">Submit</strong> - upload your beat before the timer
          fires. Miss the window and you sit the round out (and take a small honor hit).
        </li>
        <li>
          <strong className="text-foreground">Vote</strong> - submissions play back anonymously. You
          score every entry except your own; self-votes are silently dropped.
        </li>
        <li>
          <strong className="text-foreground">Results</strong> - scores tally, ranks lock, tracks
          land on profiles and genre leaderboards permanently.
        </li>
      </ol>

      <H2 id="rules-modes">Game modes</H2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-2 pr-4">Mode</th>
              <th className="py-2 pr-4">Format</th>
              <th className="py-2">The rule that matters</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-muted-foreground">
            <tr>
              <td className="py-2 pr-4 text-foreground">Quickplay</td>
              <td className="py-2 pr-4">FFA, up to 8</td>
              <td className="py-2">Auto-matched, system genre, anyone can join free.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 text-foreground">Ranked</td>
              <td className="py-2 pr-4">FFA, seeded</td>
              <td className="py-2">Honor 50+ required. Wins move your genre rating.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 text-foreground">Private rooms</td>
              <td className="py-2 pr-4">1v1 up to 4v4 or FFA</td>
              <td className="py-2">Host shares a 6-character code. Honor 30+ to host.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 text-foreground">Flip battles</td>
              <td className="py-2 pr-4">FFA</td>
              <td className="py-2">Everyone flips the same source track instead of a kit.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 text-foreground">Daily Challenge</td>
              <td className="py-2 pr-4">Async, global</td>
              <td className="py-2">
                One shared challenge per day: submit day 1, vote day 2, results day 3. 90s-4min
                track length.
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4 text-foreground">Tournaments</td>
              <td className="py-2 pr-4">Showcase + bracket</td>
              <td className="py-2">
                Honor 70+ to register. Showcase seeds round 1; single elimination after.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <H2 id="rules-voting">Voting rules</H2>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
        <li>Voting is blind: no names, no avatars, just the track.</li>
        <li>You cannot vote for yourself - self-votes are dropped server-side.</li>
        <li>
          In live battles you are expected to vote on every other entry. Submitting and then
          ghosting the vote phase costs honor.
        </li>
        <li>Vote weight scales with your honor; coordinated vote rings get detected and zeroed.</li>
        <li>Late votes (after results lock) are rejected, not silently counted.</li>
      </ul>

      <H2 id="rules-honor">The honor system</H2>
      <p className="mt-3 text-sm text-muted-foreground">
        Every account has an honor score from 0 to 100 (you start at 100). Abandoning matches,
        skipping votes, or submitting nothing drains it; clean finishes rebuild it. Honor gates the
        competitive ladder: tournaments need 70+, ranked 50+, hosting private rooms 30+. A single
        slip in 30 days is forgiven at half cost. The full penalty table is in the{' '}
        <a href="#honor" className="underline hover:text-foreground">
          FAQ
        </a>
        .
      </p>

      <H2 id="rules-fair-play">Fair play</H2>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
        <li>
          Only use the provided kit or flip source plus your own sound design. Uncleared samples get
          DMCA-struck in escalating tiers.
        </li>
        <li>Smurfing and multi-accounting in the same match is a ban.</li>
        <li>Guests can play casual modes; ranked and tournaments need a registered account.</li>
      </ul>

      <section className="mt-10 rounded-md border border-border bg-card px-4 py-4 text-sm">
        <p className="text-muted-foreground">
          New to this? Read{' '}
          <Link
            to="/blog/$slug"
            params={{ slug: 'what-is-a-beat-battle' }}
            className="underline hover:text-foreground"
          >
            what a beat battle is
          </Link>{' '}
          or{' '}
          <Link
            to="/blog/$slug"
            params={{ slug: 'how-to-win-a-beat-battle' }}
            className="underline hover:text-foreground"
          >
            how to win one
          </Link>
          , then{' '}
          <Link to="/play" className="font-bold text-foreground underline">
            jump into a battle
          </Link>{' '}
          - it is free.
        </p>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────────────────── */}
      <h2
        id="faq"
        className="mt-14 scroll-mt-24 border-t border-border pt-10 text-2xl font-bold tracking-tight"
      >
        FAQ
      </h2>
      <div className="mt-5">
        <FaqBody />
      </div>
    </article>
  );
}
